import React, { useMemo, useState } from "react";
import { limit, query } from "firebase/firestore";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { PRODUCTS } from "../../config";
import { useSession } from "../../auth/AuthProvider";
import { STATUS_META, STATUS_ORDER, compareBy } from "../../lib/licenses";
import {
  developmentCountQuery,
  expiringQuery,
  licenceCountQuery,
} from "../../lib/queries";
import { useCounts } from "../../hooks/usePaged";
import { useLiveQuery } from "../../hooks/useLive";
import { figureSx, statusColors } from "../../theme";
import { PageHeader, QuotaMeter, SectionTitle } from "../../components/ui";
import ActivityList from "../../components/ActivityList";
import LicenseDialog from "../../components/LicenseDialog";
import LicenseTable from "../../components/LicenseTable";
import { useAdminData } from "./AdminData";
import { useDistributorStats } from "./DistributorsPage";

// Counted by Firestore, so this costs the same at any number of keys.
function useProductCounts(productId) {
  const defs = Object.fromEntries(
    STATUS_ORDER.map((status) => [
      status,
      () => licenceCountQuery(productId, { status }),
    ]),
  );
  defs.development = () => developmentCountQuery(productId);
  return useCounts(`overview/${productId}`, defs);
}

function ProductSummary({ product }) {
  const { counts: raw, loading } = useProductCounts(product.id);
  const counts = Object.fromEntries(STATUS_ORDER.map((s) => [s, raw[s] || 0]));
  const total = STATUS_ORDER.reduce((sum, s) => sum + counts[s], 0);
  const devCount = raw.development || 0;
  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="h6">{product.name}</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
            >
              {product.description}
            </Typography>
            {devCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                Not counting {devCount} development{" "}
                {devCount === 1 ? "key" : "keys"}
              </Typography>
            )}
          </Box>
          <Typography sx={{ ...figureSx, fontSize: "2rem" }}>
            {loading ? "…" : total.toLocaleString()}
          </Typography>
        </Box>
        {/* Stacked bar of the status mix */}
        <Box
          sx={{
            display: "flex",
            height: 8,
            borderRadius: 4,
            overflow: "hidden",
            my: 2,
            bgcolor: "divider",
          }}
        >
          {STATUS_ORDER.map((s) =>
            counts[s] ? (
              <Box
                key={s}
                sx={{ flex: counts[s], bgcolor: statusColors[s].fg }}
                title={STATUS_META[s].label}
              />
            ) : null,
          )}
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
            gap: 0.5,
          }}
        >
          {STATUS_ORDER.map((s) => (
            <Link
              key={s}
              component={RouterLink}
              to={`/licenses/${product.path}?status=${s}`}
              underline="hover"
              color="inherit"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                fontSize: 14,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: statusColors[s].fg,
                }}
              />
              {STATUS_META[s].label}
              <Box component="span" sx={{ ml: "auto", fontWeight: 600, pr: 2 }}>
                {counts[s]}
              </Box>
            </Link>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function OverviewPage() {
  const session = useSession();
  const navigate = useNavigate();
  const { activity } = useAdminData();
  const stats = useDistributorStats();
  const [openKey, setOpenKey] = useState(null);

  // The 25 keys expiring soonest, across both products.
  const [first, second] = PRODUCTS;
  const expiringA = useLiveQuery(
    `expiring/${first.id}`,
    () => query(expiringQuery(first.id), limit(25)),
    {
      product: first.id,
    },
  );
  const expiringB = useLiveQuery(
    `expiring/${second.id}`,
    () => query(expiringQuery(second.id), limit(25)),
    {
      product: second.id,
    },
  );
  const expiring = useMemo(
    () =>
      [...expiringA.data, ...expiringB.data]
        .sort(compareBy("expirySort", "asc"))
        .slice(0, 25),
    [expiringA.data, expiringB.data],
  );
  const openLicense = openKey
    ? expiring.find((l) => `${l.product}/${l.id}` === openKey)
    : null;

  return (
    <>
      <PageHeader
        eyebrow="LiFE KeyPortal"
        title="Overview"
        subtitle="Licence keys across both products, distributor quotas and recent activity."
      />

      <Box
        sx={{ display: "grid", gap: 2, gridTemplateColumns: { md: "1fr 1fr" } }}
      >
        {PRODUCTS.map((p) => (
          <ProductSummary key={p.id} product={p} />
        ))}
      </Box>

      <SectionTitle
        action={
          <Button component={RouterLink} to="/distributors" size="small">
            All distributors
          </Button>
        }
      >
        Distributor quotas
      </SectionTitle>
      {stats.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, color: "text.secondary" }}>
          No distributors yet.{" "}
          <Link component={RouterLink} to="/distributors">
            Add one
          </Link>{" "}
          to start allocating keys.
        </Paper>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Distributor</TableCell>
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    Region
                  </TableCell>
                  <TableCell sx={{ width: { sm: 220 } }}>Quota used</TableCell>
                  <TableCell align="right">Left</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...stats]
                  .sort((a, b) => b.totals.issued - a.totals.issued)
                  .slice(0, 8)
                  .map((d) => (
                    <TableRow
                      key={d.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`/distributors/${d.id}`)}
                    >
                      <TableCell>{d.name}</TableCell>
                      <TableCell
                        sx={{ display: { xs: "none", sm: "table-cell" } }}
                      >
                        {d.region}
                      </TableCell>
                      <TableCell>
                        <QuotaMeter
                          allocated={d.totals.allocated}
                          issued={d.totals.issued}
                        />
                      </TableCell>
                      <TableCell align="right">{d.totals.remaining}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <SectionTitle>Expiring soonest</SectionTitle>
      <LicenseTable
        rows={expiring}
        columns={["key", "productType", "distributor", "customer", "expiry"]}
        onRowClick={(l) => setOpenKey(`${l.product}/${l.id}`)}
        emptyText="Nothing expires in the next 3 months."
        initialRowsPerPage={25}
      />

      <SectionTitle>Recent activity</SectionTitle>
      <ActivityList entries={activity.slice(0, 20)} />

      {openLicense && (
        <LicenseDialog
          license={openLicense}
          canManage
          actor={session.email}
          onClose={() => setOpenKey(null)}
        />
      )}
    </>
  );
}
